<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import MergeStatusBanner from './MergeStatusBanner.vue';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
  lockToSingleConversation: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const { t } = useI18n();
const store = useStore();
const isLoading = ref(false);
const duplicateStats = ref(null);
const isFetchingStats = ref(false);
const isExpanded = ref(false);

// Fetch statistics about duplicate conversations
const fetchDuplicateStats = async () => {
  try {
    isFetchingStats.value = true;
    
    const response = await store.dispatch('inboxes/getDuplicateConversations', {
      inboxId: props.inbox.id,
    });
    
    duplicateStats.value = response.data;
  } catch (error) {
    console.error('[MERGE-CONVERSATIONS] Error fetching stats:', error);
    duplicateStats.value = null;
  } finally {
    isFetchingStats.value = false;
  }
};

// Merge all duplicate conversations
const mergeConversations = async () => {
  console.log('[MERGE-CONVERSATIONS] mergeConversations called', {
    duplicateStats: duplicateStats.value,
    inboxId: props.inbox.id
  });

  if (!duplicateStats.value || duplicateStats.value.total_duplicates === 0) {
    useAlert(t('API_CONVERSATIONS_MERGE.MERGE_CONVERSATIONS.NO_DUPLICATES'));
    return;
  }

  if (!confirm(t('API_CONVERSATIONS_MERGE.MERGE_CONVERSATIONS.CONFIRM_MESSAGE', {
    count: duplicateStats.value.total_duplicates,
  }))) {
    console.log('[MERGE-CONVERSATIONS] User cancelled merge');
    return;
  }

  console.log('[MERGE-CONVERSATIONS] Starting merge operation...');

  try {
    isLoading.value = true;

    const result = await store.dispatch('inboxes/mergeDuplicateConversations', {
      inboxId: props.inbox.id,
    });

    console.log('[MERGE-CONVERSATIONS] Merge completed:', result);

    useAlert(t('API_CONVERSATIONS_MERGE.MERGE_CONVERSATIONS.SUCCESS_MESSAGE'));
    
    // Refresh stats after merge
    await fetchDuplicateStats();
  } catch (error) {
    console.error('[MERGE-CONVERSATIONS] Error merging:', error);
    useAlert(t('API_CONVERSATIONS_MERGE.MERGE_CONVERSATIONS.ERROR_MESSAGE'));
  } finally {
    isLoading.value = false;
  }
};

// ---- Duplicate CONTACTS (BR 8/9-digit variant) ----
const contactStats = ref(null);
const isFetchingContactStats = ref(false);
const isMergingContacts = ref(false);
const contactMergeStatus = ref(null);
let contactStatusTimer = null;

const fetchContactStats = async () => {
  try {
    isFetchingContactStats.value = true;
    const response = await store.dispatch('inboxes/getDuplicateContacts', {
      inboxId: props.inbox.id,
    });
    contactStats.value = response.data;
  } catch (error) {
    console.error('[MERGE-CONTACTS] Error fetching stats:', error);
    contactStats.value = null;
  } finally {
    isFetchingContactStats.value = false;
  }
};

const pollContactMergeStatus = async () => {
  try {
    const response = await store.dispatch('inboxes/getDuplicateContactsStatus', {
      inboxId: props.inbox.id,
    });
    contactMergeStatus.value = response.data;
    if (response.data?.status === 'running') {
      contactStatusTimer = setTimeout(pollContactMergeStatus, 3000);
    } else if (response.data?.status === 'completed') {
      await fetchContactStats();
    }
  } catch (error) {
    console.error('[MERGE-CONTACTS] Error fetching status:', error);
  }
};

const mergeContacts = async () => {
  const count = contactStats.value?.duplicate_contact_pairs || 0;
  if (!count) {
    useAlert(t('API_CONVERSATIONS_MERGE.MERGE_CONTACTS.NO_DUPLICATES'));
    return;
  }
  if (!confirm(t('API_CONVERSATIONS_MERGE.MERGE_CONTACTS.CONFIRM_MESSAGE', { count }))) {
    return;
  }
  try {
    isMergingContacts.value = true;
    await store.dispatch('inboxes/mergeDuplicateContacts', { inboxId: props.inbox.id });
    useAlert(t('API_CONVERSATIONS_MERGE.MERGE_CONTACTS.SUCCESS_MESSAGE'));
    pollContactMergeStatus();
  } catch (error) {
    console.error('[MERGE-CONTACTS] Error merging:', error);
    useAlert(t('API_CONVERSATIONS_MERGE.MERGE_CONTACTS.ERROR_MESSAGE'));
  } finally {
    isMergingContacts.value = false;
  }
};

const hasDuplicateContacts = computed(() => {
  return contactStats.value && contactStats.value.duplicate_contact_pairs > 0;
});

// Computed property for display
const hasDuplicates = computed(() => {
  return duplicateStats.value && duplicateStats.value.total_duplicates > 0;
});

// Channel::Quepasa is a dedicated channel type, not a Channel::Api
// specialization, so the plain equality check hid this panel from every
// QuePasa Channel inbox - the exact inboxes that accumulate duplicates.
const SUPPORTED_CHANNEL_TYPES = ['Channel::Api', 'Channel::Quepasa'];

const isApiInbox = computed(() =>
  SUPPORTED_CHANNEL_TYPES.includes(props.inbox?.channel_type)
);

// Show component only for API inboxes with lock enabled
const shouldShow = computed(() => {
  return isApiInbox.value && props.lockToSingleConversation;
});

// Fetch stats on mount
onMounted(() => {
  if (shouldShow.value) {
    fetchDuplicateStats();
    fetchContactStats();
  }
});

onUnmounted(() => {
  if (contactStatusTimer) clearTimeout(contactStatusTimer);
});

// Watch for changes in shouldShow (when props are ready)
watch(shouldShow, (newValue) => {
  if (newValue && !duplicateStats.value) {
    fetchDuplicateStats();
  }
}, { immediate: true });
</script>

<template>
  <div v-if="shouldShow" class="mt-6 pt-6 border-t border-n-slate-6">
    <button
      class="w-full flex items-center justify-between mb-4 text-left group"
      @click="isExpanded = !isExpanded"
    >
      <div class="flex-1">
        <h3 class="text-base font-medium text-n-slate-12 group-hover:text-n-brand transition-colors">
          {{ $t('API_CONVERSATIONS_MERGE.MERGE_CONVERSATIONS.TITLE') }}
        </h3>
        <p class="mt-1 text-sm text-n-slate-11">
          {{ $t('API_CONVERSATIONS_MERGE.MERGE_CONVERSATIONS.DESCRIPTION') }}
        </p>
      </div>
      <span class="text-n-slate-10 ml-4 transition-transform" :class="{ 'rotate-180': isExpanded }">
        ▼
      </span>
    </button>

    <div v-show="isExpanded" class="space-y-4">

    <!-- Status Banner -->
    <MergeStatusBanner :inbox-id="inbox.id" />

    <!-- Loading stats -->
    <div v-if="isFetchingStats" class="flex items-center gap-2 text-sm text-n-slate-11">
      <span class="animate-spin">⏳</span>
      {{ $t('API_CONVERSATIONS_MERGE.MERGE_CONVERSATIONS.LOADING_STATS') }}
    </div>

    <!-- Stats display -->
    <div v-else-if="duplicateStats" class="space-y-4">
      <!-- Stats card -->
      <div class="p-4 bg-n-slate-2 border border-n-slate-6 rounded-lg">
        <div class="grid grid-cols-2 gap-4 text-sm">
          <div>
            <div class="text-n-slate-11">
              {{ $t('API_CONVERSATIONS_MERGE.MERGE_CONVERSATIONS.TOTAL_CONTACTS') }}
            </div>
            <div class="text-lg font-semibold text-n-slate-12">
              {{ duplicateStats.total_contacts || 0 }}
            </div>
          </div>
          <div>
            <div class="text-n-slate-11">
              {{ $t('API_CONVERSATIONS_MERGE.MERGE_CONVERSATIONS.DUPLICATE_CONVERSATIONS') }}
            </div>
            <div class="text-lg font-semibold" :class="hasDuplicates ? 'text-yellow-600' : 'text-green-600'">
              {{ duplicateStats.total_duplicates || 0 }}
            </div>
          </div>
        </div>
      </div>

      <!-- Merge button -->
      <div v-if="hasDuplicates" class="flex items-center gap-3">
        <NextButton
          type="button"
          :label="$t('API_CONVERSATIONS_MERGE.MERGE_CONVERSATIONS.MERGE_BUTTON', {
            count: duplicateStats.total_duplicates,
          })"
          :is-loading="isLoading"
          variant="solid"
          color="ruby"
          @click="mergeConversations"
        />
        <NextButton
          type="button"
          :label="$t('API_CONVERSATIONS_MERGE.MERGE_CONVERSATIONS.REFRESH_BUTTON')"
          :is-loading="isFetchingStats"
          variant="outline"
          @click="fetchDuplicateStats"
        />
      </div>

      <!-- No duplicates message -->
      <div v-else class="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
        <span>✅</span>
        <span>{{ $t('API_CONVERSATIONS_MERGE.MERGE_CONVERSATIONS.NO_DUPLICATES') }}</span>
      </div>
    </div>

    <!-- Duplicate CONTACTS (BR 8/9-digit variant) -->
    <div class="pt-4 border-t border-n-slate-6 space-y-4">
      <div>
        <h4 class="text-sm font-medium text-n-slate-12">
          {{ $t('API_CONVERSATIONS_MERGE.MERGE_CONTACTS.TITLE') }}
        </h4>
        <p class="mt-1 text-sm text-n-slate-11">
          {{ $t('API_CONVERSATIONS_MERGE.MERGE_CONTACTS.DESCRIPTION') }}
        </p>
      </div>

      <div v-if="isFetchingContactStats" class="flex items-center gap-2 text-sm text-n-slate-11">
        <span class="animate-spin">⏳</span>
        {{ $t('API_CONVERSATIONS_MERGE.MERGE_CONVERSATIONS.LOADING_STATS') }}
      </div>

      <div v-else-if="contactStats" class="space-y-4">
        <div class="p-4 bg-n-slate-2 border border-n-slate-6 rounded-lg">
          <div class="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div class="text-n-slate-11">
                {{ $t('API_CONVERSATIONS_MERGE.MERGE_CONVERSATIONS.TOTAL_CONTACTS') }}
              </div>
              <div class="text-lg font-semibold text-n-slate-12">
                {{ contactStats.total_contacts || 0 }}
              </div>
            </div>
            <div>
              <div class="text-n-slate-11">
                {{ $t('API_CONVERSATIONS_MERGE.MERGE_CONTACTS.DUPLICATE_PAIRS') }}
              </div>
              <div class="text-lg font-semibold" :class="hasDuplicateContacts ? 'text-yellow-600' : 'text-green-600'">
                {{ contactStats.duplicate_contact_pairs || 0 }}
              </div>
            </div>
          </div>
        </div>

        <!-- running status -->
        <div
          v-if="contactMergeStatus && contactMergeStatus.status === 'running'"
          class="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800"
        >
          {{ $t('API_CONVERSATIONS_MERGE.MERGE_CONTACTS.RUNNING', {
            progress: contactMergeStatus.progress || 0,
            merged: contactMergeStatus.merged_count || 0,
          }) }}
        </div>

        <div v-if="hasDuplicateContacts" class="flex items-center gap-3">
          <NextButton
            type="button"
            :label="$t('API_CONVERSATIONS_MERGE.MERGE_CONTACTS.MERGE_BUTTON', {
              count: contactStats.duplicate_contact_pairs,
            })"
            :is-loading="isMergingContacts"
            variant="solid"
            color="ruby"
            @click="mergeContacts"
          />
          <NextButton
            type="button"
            :label="$t('API_CONVERSATIONS_MERGE.MERGE_CONVERSATIONS.REFRESH_BUTTON')"
            :is-loading="isFetchingContactStats"
            variant="outline"
            @click="fetchContactStats"
          />
        </div>

        <div v-else class="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
          <span>✅</span>
          <span>{{ $t('API_CONVERSATIONS_MERGE.MERGE_CONTACTS.NO_DUPLICATES') }}</span>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>
